import { useState } from 'react';
import { useImageState, useImageActions } from '../../contexts/ImageContext';
import { FaPaintBrush, FaEraser, FaTrash, FaTimes, FaDownload, FaUndo, FaPalette, FaImage } from 'react-icons/fa';
import { AlertCircle, X, ChevronDown } from 'lucide-react';
import { cn } from '../../utils';
import { type InpaintingCanvasRef } from './InpaintingCanvas';


type BrushColor = 'red' | 'blue' | 'green';

interface ImageHeaderProps {
  readonly className?: string;
  readonly onClose?: () => void;
  readonly readOnly?: boolean;
  readonly getSelectedImageUrl?: () => string;
  readonly canvasRef?: React.RefObject<InpaintingCanvasRef>;
  readonly brushColor?: BrushColor;
  readonly onBrushColorChange?: (color: BrushColor) => void;
  readonly onPrintScale?: () => void;
  readonly isPrintScaleActive?: boolean; // NEW: highlight state from parent
}

export function ImageHeader({ 
  className = "", 
  onClose, 
  readOnly = false,
  getSelectedImageUrl,
  canvasRef,
  brushColor = 'red',
  onBrushColorChange,
  onPrintScale,
  isPrintScaleActive = false // NEW: default to false
}: ImageHeaderProps) {
  const { selectedImage, inpainting, isLocalInpaintingOpen, localUploadImageUrl } = useImageState();  
  const { deselectImage, setInpaintingActive, setEraserMode, clearInpainting } = useImageActions();
  const [notification, setNotification] = useState<{ message: string; type: 'error' | 'warning' } | null>(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  // Removed unused fileInputRef and related logic

  // Helper function to show notifications
  const showNotification = (message: string, type: 'error' | 'warning' = 'error') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  // Helper function to check if mask data exists for operations
  const hasMaskData = () => Boolean(inpainting.maskData);

  // Check if action is allowed (not read-only and has mask data if required)
  const canPerformAction = (requiresMask = false) => {
    if (readOnly) return false;
    if (requiresMask && !hasMaskData()) return false;
    return true;
  };

  // Get disabled reason for tooltip
  const getDisabledReason = (requiresMask = false) => {
    if (readOnly) return "Read-only mode";
    if (requiresMask && !hasMaskData()) return "No inpainting to perform action on";
    return "";
  };

  // Extracted inpainting action handlers
  const handleInpaintingActions = {
    selectInpainting: () => {
      if (!canPerformAction()) {
        showNotification('Inpainting is disabled in read-only mode', 'warning');
        return;
      }
      setInpaintingActive(true);
      setEraserMode(false);
    },

    eraseInpainting: () => {
      if (!canPerformAction(true)) {
        const reason = getDisabledReason(true);
        showNotification(reason === "Read-only mode" ? 'Eraser is disabled in read-only mode' : 'No inpainting to erase. Paint something first!', 'warning');
        return;
      }
      
      const newState = !inpainting.isEraserMode;
      setEraserMode(newState);
      
      if (newState && !inpainting.isActive) {
        setInpaintingActive(true);
      }
    },

    clearInpainting: () => {
      if (!canPerformAction(true)) {
        const reason = getDisabledReason(true);
        showNotification(reason === "Read-only mode" ? 'Clear is disabled in read-only mode' : 'No inpainting to clear. Paint something first!', 'warning');
        return;
      }
      
      canvasRef?.current?.clearMask();
      clearInpainting();
      setInpaintingActive(false);
    },

    exitInpainting: () => {
      if (!canPerformAction()) return;
      
      canvasRef?.current?.clearMask();
      clearInpainting();
      setInpaintingActive(false);
    },

    undo: () => {
      if (!canPerformAction(true)) {
        const reason = getDisabledReason(true);
        showNotification(reason === "Read-only mode" ? 'Undo is disabled in read-only mode' : 'No inpainting to undo. Paint something first!', 'warning');
        return;
      }
      
      canvasRef?.current?.undo();
    }
  };

  // Get tooltip text for brush color button
  const getBrushColorTooltip = () => {
    if (!canPerformAction()) return getDisabledReason();
    if (!inpainting.isActive) return "Activate inpainting mode first";
    return `Brush color: ${getColorInfo(brushColor).name}`;
  };

  // Get CSS class for brush color button
  const getBrushColorButtonClass = () => {
    if (!canPerformAction() || !inpainting.isActive) {
      return 'opacity-50 cursor-not-allowed text-gray-400 dark:text-gray-500';
    }
    return 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600';
  };

  const handleColorChange = (color: BrushColor) => {
    if (onBrushColorChange) {
      onBrushColorChange(color);
    }
    setShowColorPicker(false);
  };

  const getColorInfo = (color: BrushColor) => {
    switch (color) {
      case 'red': return { name: 'Red', rgb: 'rgb(220, 38, 38)', bg: 'bg-red-500' };
      case 'blue': return { name: 'Blue', rgb: 'rgb(70, 130, 220)', bg: 'bg-blue-500' };
      case 'green': return { name: 'Green', rgb: 'rgb(34, 197, 94)', bg: 'bg-green-500' };
      default: return { name: 'Red', rgb: 'rgb(220, 38, 38)', bg: 'bg-red-500' };
    }
  };

  const handleClose = () => {
    // Always exit inpainting mode first if active
    if (inpainting.isActive) {
      setInpaintingActive(false);
      clearInpainting(); // Also clear any mask data
    }
    
    if (onClose) {
      onClose();
    } else {
      deselectImage();
    }
  };

  // Button state helpers
  const getBaseButtonClass = (isActive = false, activeClass = '', disabledClass = '') => {
    const baseClass = 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600';
    const disabled = 'opacity-50 cursor-not-allowed text-gray-400 dark:text-gray-500';
    
    if (readOnly) return disabled;
    if (disabledClass && !hasMaskData()) return disabled;
    if (isActive && activeClass) return activeClass;
    return baseClass;
  };

  const getInpaintingButtonClass = () => 
    getBaseButtonClass(
      inpainting.isActive && !inpainting.isEraserMode,
      'bg-blue-500 text-white hover:bg-blue-600'
    ) + ' hover:text-blue-600 dark:hover:text-blue-400';

  const getEraserButtonClass = () => 
    getBaseButtonClass(
      inpainting.isEraserMode,
      'bg-red-500 text-white hover:bg-red-600',
      'mask-required'
    ) + ' hover:text-red-600 dark:hover:text-red-400';

  const getClearButtonClass = () => 
    getBaseButtonClass(false, '', 'mask-required') + ' hover:text-orange-600 dark:hover:text-orange-400';

  const getUndoButtonClass = () => 
    getBaseButtonClass(false, '', 'mask-required') + ' hover:text-purple-600 dark:hover:text-purple-400';

  // Helper function to get exit inpainting button tooltip text
  const getExitInpaintingTooltip = () => {
    if (!canPerformAction()) {
      return getDisabledReason();
    }
    
    if (!inpainting.isActive) {
      return "Activate inpainting mode first";
    }
    
    return "Exit inpainting mode";
  };

  const handleDownload = () => {
    // Get selected chat image URL if image is selected and function available
    const selectedImageUrlForDownload = selectedImage !== -1 && getSelectedImageUrl ? getSelectedImageUrl() : '';
    const imageUrl = isLocalInpaintingOpen && localUploadImageUrl ? localUploadImageUrl : selectedImageUrlForDownload;

    if (!imageUrl) {
      showNotification('Cannot download image. No image selected or download function unavailable.');
      return;
    }
     try {
      const link = document.createElement('a');
      link.href = imageUrl;
      
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('.')[0];
      const inferExtension = (url: string) => {
        if (url.startsWith('data:')) {
          const mimeMatch = /^data:([^;]+);/i.exec(url);
          const mime = mimeMatch?.[1]?.toLowerCase();
          switch (mime) {
            case 'image/png':
              return 'png';
            case 'image/jpeg':
            case 'image/jpg':
              return 'jpg';
            case 'image/webp':
              return 'webp';
            case 'image/gif':
              return 'gif';
            case 'image/avif':
              return 'avif';
            case 'image/bmp':
            case 'image/x-ms-bmp':
              return 'bmp';
            default:
              return '';
          }
        }

        if (url.startsWith('blob:')) {
          // Blob URLs don't encode file extension; omit to avoid mismatched filenames.
          return '';
        }

        const cleanUrl = url.split('?')[0].split('#')[0];
        const extMatch = /\.([a-z0-9]+)$/i.exec(cleanUrl);
        const ext = extMatch?.[1]?.toLowerCase();
        if (!ext) return '';
        if (!['png', 'jpg', 'jpeg', 'webp', 'gif', 'avif', 'bmp'].includes(ext)) {
          return '';
        }
        return ext === 'jpeg' ? 'jpg' : ext;
      };

      const extension = inferExtension(imageUrl);
      link.download = extension ? `image-${timestamp}.${extension}` : `image-${timestamp}`;
      
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading image:', error);
      showNotification('Failed to download image. Please try again.');
    }
  };

  // Render helpers for complex UI sections
  const renderNotification = () => {
    if (!notification) return null;
    
    const isError = notification.type === 'error';
    const bgClass = isError 
      ? "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200"
      : "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200";

    return (
      <div className="absolute top-2 right-2 left-2 z-20 animate-in slide-in-from-top-2">
        <div className={cn("flex items-center gap-3 p-3 rounded-lg shadow-lg border", bgClass)}>
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm font-medium flex-1">{notification.message}</span>
          <button
            onClick={() => setNotification(null)}
            className="text-current hover:opacity-70 transition-opacity"
            aria-label="Dismiss notification"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  const renderColorPicker = () => {
    if (!showColorPicker || readOnly || !inpainting.isActive) return null;

    return (
      <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-30">
        <div className="p-2 space-y-1">
          {(['red', 'blue', 'green'] as const).map((color) => {
            const colorInfo = getColorInfo(color);
            const isSelected = brushColor === color;
            return (
              <button
                key={color}
                onClick={() => handleColorChange(color)}
                className={`w-full flex items-center space-x-2 px-3 py-2 rounded text-sm transition-colors ${
                  isSelected
                    ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300'
                }`}
              >
                <div className={`w-4 h-4 rounded-full ${colorInfo.bg}`}></div>
                <span>{colorInfo.name}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Helper to render brush color selector
  const renderBrushColorSelector = () => (
    <div className="relative">
      <button 
        onClick={canPerformAction() && inpainting.isActive ? () => setShowColorPicker(!showColorPicker) : undefined}
        disabled={!canPerformAction() || !inpainting.isActive}
        title={getBrushColorTooltip()}
        className={`p-2 rounded transition-colors ${getBrushColorButtonClass()}`}
      >
        <div className="flex items-center space-x-1">
          <FaPalette className="w-4 h-4" />
          <div className={`w-3 h-3 rounded-full ${getColorInfo(brushColor).bg} ${
            inpainting.isActive ? '' : 'opacity-50'
          }`}></div>
          <ChevronDown className="w-3 h-3" />
        </div>
      </button>
      {renderColorPicker()}
    </div>
  );

  // Helper to get exit button class
  const getExitButtonClass = () => {
    const isDisabled = !(isPrintScaleActive || (inpainting.isActive && canPerformAction()));
    return isDisabled
      ? 'opacity-50 cursor-not-allowed text-gray-400 dark:text-gray-500'
      : 'text-orange-600 dark:text-orange-400 hover:bg-orange-100 dark:hover:bg-orange-900/20 hover:text-orange-700 dark:hover:text-orange-300';
  };

  // Helper to render exit/cancel button for inpainting or print scale
  const handleExit = () => {
    if (inpainting.isActive && canPerformAction()) {
      handleInpaintingActions.exitInpainting();
    } else if (isPrintScaleActive) {
      // Reset Print Scale overlay and image in ImageViewer, but do NOT close the viewer
      if (globalThis.window?.dispatchEvent !== undefined) {
        globalThis.window.dispatchEvent(new CustomEvent('resetPrintScaleOverlay'));
      }
    }
  };

  const renderExitButton = () => (
    <button
      onClick={handleExit}
      disabled={!(isPrintScaleActive || (inpainting.isActive && canPerformAction()))}
      title={inpainting.isActive ? getExitInpaintingTooltip() : 'Cancel print scale'}
      className={`p-2 rounded transition-colors ${getExitButtonClass()}`}
    >
      {/*
        Disabled unless:
        - Print Scale is active
        - OR inpainting is active AND canPerformAction() is true
        This ensures the button is only enabled when clicking will actually perform an exit/cancel action.
      */}
      <X className="w-4 h-4" strokeWidth={5} />
    </button>
  );

  const handlePrintScale = () => {
    if (onPrintScale) onPrintScale();
  };

  // Render inpainting toolbar section
  const renderInpaintingTools = () => (
    <div className="flex items-center space-x-2">
      <button 
        onClick={canPerformAction() ? handleInpaintingActions.selectInpainting : undefined}
        disabled={!canPerformAction()}
        title={getDisabledReason() || "Select inpainting"}
        className={`p-2 rounded transition-colors ${getInpaintingButtonClass()}`}
      >
        <FaPaintBrush className="w-4 h-4" />
      </button>

      {renderBrushColorSelector()}

      <button 
        onClick={canPerformAction(true) ? handleInpaintingActions.eraseInpainting : undefined}
        disabled={!canPerformAction(true)}
        title={getDisabledReason(true) || "Erase inpainting"}
        className={`p-2 rounded transition-colors ${getEraserButtonClass()}`}
      >
        <FaEraser className="w-4 h-4" />
      </button>

      <button 
        onClick={canPerformAction(true) ? handleInpaintingActions.undo : undefined}
        disabled={!canPerformAction(true)}
        title={getDisabledReason(true) || "Undo last action"}
        className={`p-2 rounded transition-colors ${getUndoButtonClass()}`}
      >
        <FaUndo className="w-4 h-4" />
      </button>

      <button 
        onClick={canPerformAction(true) ? handleInpaintingActions.clearInpainting : undefined}
        disabled={!canPerformAction(true)}
        title={getDisabledReason(true) || "Clear inpainting"}
        className={`p-2 rounded transition-colors ${getClearButtonClass()}`}
      >
        <FaTrash className="w-4 h-4" />
      </button>

      {/* Print Scale Button with monochrome image icon */}
      <button
        onClick={handlePrintScale}
        title="Print Scale"
        className={`p-2 rounded transition-colors ${isPrintScaleActive ? 'bg-indigo-500 text-white' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
      >
        <FaImage style={{ color: isPrintScaleActive ? 'white' : '#6b7280', width: 24, height: 24 }} />
      </button>

  {renderExitButton()}
    </div>
  );

  // Render action buttons section
  const renderActionButtons = () => (
    <div className="flex items-center space-x-2">
      <button 
        onClick={handleDownload}
        title="Download image"
        className="p-2 text-gray-600 dark:text-gray-300 hover:bg-green-100 dark:hover:bg-green-900/20 rounded transition-colors hover:text-green-600 dark:hover:text-green-400"
      >
        <FaDownload className="w-4 h-4" />
      </button>
      <button 
        onClick={handleClose}
        title="Close image viewer"
        className="p-2 text-gray-600 dark:text-gray-300 hover:bg-red-100 dark:hover:bg-red-900/20 rounded transition-colors hover:text-red-600 dark:hover:text-red-400"
      >
        <FaTimes className="w-4 h-4" />
      </button>
    </div>
  );

  return (
    <div className={`px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 ${className} relative`}>
      {renderNotification()}

      <div className="flex items-center justify-between">
        {/* Left side - Inpainting tools */}
        {renderInpaintingTools()}

        {/* Right side - Download and Close buttons */}
        {renderActionButtons()}
      </div>

      {/* Overlay Print Scale image removed, now handled in parent */}
    </div>
  );
}
